import { useRef, useEffect } from "react"
import { Pressable } from "react-native"
import { ar } from "@src/i18n/ar"
import { dayjs } from "@src/utils/dayjs"
import { MCIcon, Text } from "./core"
import { DatePickerProps } from "./date-picker"

export function DatePicker({ className, value, onChange, placeholder = ar.selectDate, mode = "date" }: DatePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  // Convert Date to input format
  const getInputValue = (date?: Date) => {
    if (!date) return ""
    if (mode === "date") {
      return dayjs(date).format("YYYY-MM-DD")
    }
    // datetime-local format: YYYY-MM-DDTHH:mm
    return dayjs(date).format("YYYY-MM-DDTHH:mm")
  }

  // Format date for display
  const formatDate = (date: Date) => {
    if (mode === "date") {
      return dayjs(date).format("D MMMM، YYYY")
    }
    return dayjs(date).format("D MMMM، YYYY h:mm A")
  }

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const newValue = target.value

    if (!newValue) {
      onChange(undefined)
      return
    }

    try {
      const date = new Date(newValue)
      if (!Number.isNaN(date.getTime())) {
        onChange(date)
      }
    } catch (error) {
      console.error("Invalid date:", error)
    }
  }

  const clearDate = (e: unknown) => {
    if (e && typeof e === "object" && "stopPropagation" in e && typeof e.stopPropagation === "function") {
      e.stopPropagation()
    }
    onChange(undefined)
    if (inputRef.current) {
      inputRef.current.value = ""
    }
  }

  const openPicker = () => {
    if (inputRef.current) {
      inputRef.current.showPicker?.()
    }
  }

  useEffect(() => {
    const input = inputRef.current
    if (input) {
      input.addEventListener("change", handleChange)
      return () => {
        input.removeEventListener("change", handleChange)
      }
    }
  }, [])

  return (
    <Pressable
      onPress={openPicker}
      className={`flex-row ps-2 items-center justify-between bg-white border border-gray-300 rounded hover:bg-gray-50 ${className}`}
      style={{ height: 50, position: "relative" }}
    >
      <Text className={`flex-1 text-xs text-left ${value ? "text-gray-800" : "text-gray-400"}`}>{value ? formatDate(value) : placeholder}</Text>

      {value && (
        <Pressable onPress={clearDate} className="justify-center items-center h-[50px] px-2 hover:bg-gray-100 rounded-r" hitSlop={10} style={{ zIndex: 10 }}>
          <MCIcon name="close" size={20} color="#6B7280" />
        </Pressable>
      )}

      {/* Hidden native input */}
      <input
        ref={inputRef as unknown as React.RefObject<HTMLInputElement>}
        type={mode === "date" ? "date" : "datetime-local"}
        defaultValue={getInputValue(value)}
        style={{
          position: "absolute",
          opacity: 0,
          width: 1,
          height: 1,
          pointerEvents: "none",
        }}
        aria-label={placeholder}
      />
    </Pressable>
  )
}
